/*@flow*/
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import React, { type ElementConfig } from 'react';
import { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import filterByLabel from 'lib/filterByLabel';
import Blank from 'lib/Blank';
import * as I from 'immutable';
import useCachedQuery from 'lib/useCachedQuery';
import { GetIntersectingResources } from './queries/GetIntersectingResources';
import { GetResourceList } from '../queries/GetResourceList';

export type Option = { value: any, label: string };

const customStyles = {
  input: (base: Object) => ({ ...base, border: 'none' }),
  menu: (base: Object) => ({ ...base, maxWidth: 400 }),
  valueContainer: (base: Object) => ({ ...base, paddingLeft: 0 }),
  control: (base: Object) => ({
    ...base,
    border: 'none',
    borderBottom: 'solid 1px #ccc',
    borderRadius: 0,
    maxWidth: 400,
    boxShadow: 'none',
  }),
};

export type PickerProps = {
  theGeom: string,
  id?: ?(ID | ID[]),
  selectProps?: { [key: string]: any },
  onChange: (Option | Option[]) => any,
};

export function mkComponent(props: {
  placeholder: string,
  targetRelation: string,
}) {
  return (nextProps: PickerProps) => {
    if (!nextProps.theGeom) {
      return (
        <div
          data-testid="DisabledGeoResourcePicker"
          css={css('cursor: not-allowed; position: relative;')}
        >
          <input
            type="text"
            disabled
            placeholder={props.placeholder}
            className="form-control input-sm p-3 rounded border"
            css={css('font-size: 1.6rem;')}
          />
          <i
            css={css(
              `
              border: solid 1px white;
              position: absolute;
              right: 40px;
              bottom: 0px;
              top: 0px;
            `
            )}
          />
        </div>
      );
    }

    return (
      <Resources
        placeholder={props.placeholder}
        targetRelation={props.targetRelation}
        id={nextProps.id}
        theGeom={nextProps.theGeom}
        selectProps={nextProps.selectProps}
        onChange={nextProps.onChange}
      />
    );
  };
}

const DropdownIndicator = (
  props: ElementConfig<typeof components.DropdownIndicator>
) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="fal fa-map-marker-smile" css={css('color: #666;')} />
    </components.DropdownIndicator>
  );
};

function getNewOptsFilter(data, key = 'intersectingResources') {
  const newOpts = I.getIn(data, ['me', 'company', key], []).map((v) => ({
    value: v.id,
    label: v.name,
  }));
  const ids = newOpts.map((v) => String(v.value));
  const filter = (opts) => {
    return opts.filter((v2) => !ids.includes(String(v2.value))).concat(newOpts);
  };
  return { filter };
}

export function Resources(props: {
  targetRelation: string,
  placeholder?: string,
  id?: ?(ID | ID[]),
  theGeom?: string,
  onChange: (Option | Option[]) => any,
  selectProps?: {| [key: string]: any |},
}) {
  const {
    placeholder,
    theGeom,
    id,
    targetRelation,
    onChange,
    selectProps,
  } = props;

  const [options, setOptions] = useState([]);

  const { data, loading, error } = useCachedQuery(GetIntersectingResources, {
    variables: {
      theGeom,
      targetRelation,
    },
  });

  const idSet = useCachedQuery(GetResourceList, {
    variables: {
      id: Array.isArray(id) ? id : [id],
      resourceName: targetRelation,
    },
    skip: Blank.is(id) || Blank.is(id),
  });

  useEffect(() => {
    if (loading) return;
    const { filter } = getNewOptsFilter(data);
    setOptions((v) => filter(v));
  }, [data, loading]);

  useEffect(() => {
    if (idSet.loading) return;
    const { filter } = getNewOptsFilter(idSet.data, 'resources');
    setOptions((v) => filter(v));
  }, [idSet.data, idSet.loading]);

  const targetOption = options[0];

  return (
    <React.Fragment>
      {idSet.error && (
        <div className="text-danger">{idSet.error.toString()}</div>
      )}
      {error && <div className="text-danger">{error.toString()}</div>}
      <Select
        {...selectProps || {}}
        placeholder={placeholder || 'Select...'}
        isMulti={false}
        options={options}
        isClearable
        cacheOptions
        components={{ DropdownIndicator }}
        filterOption={filterByLabel}
        onChange={onChange}
        isLoading={loading}
        value={targetOption}
        styles={customStyles}
      />
    </React.Fragment>
  );
}
